import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { InfoBanner } from "@/components/ui/banner";
import { Stepper } from "@/components/ui/stepper";
import { Field, Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/form-controls";
import { toast } from "@/components/ui/toast";
import { cn } from "@/lib/utils";
import { usePermission } from "@/hooks/use-permission";
import { createWallet as createWalletRequest, fetchWalletOptions } from "@/lib/api";
import { WALLET_ASSETS, WALLET_GROUPS, WALLET_NETWORKS, WALLET_PROVIDERS } from "@/data/mock";
import { useWalletStore } from "@/stores/wallet-store";
import type { WalletCreationEvent, WalletRequestValues, WalletType } from "@/types";
import type { WalletRequestStep } from "@/features/wallets/wallet-request-step";

const STEPS = ["Configuration", "Network & Asset", "Provider Route", "Policy & Confirm"] as const;

const TYPE_CARDS: { value: WalletType; label: string; description: string }[] = [
  { value: "hot", label: "Hot", description: "Connected signing · operational wallets" },
  { value: "warm", label: "Warm", description: "Approval required before signing" },
  { value: "cold", label: "Cold", description: "Air-gapped · multi-sig + HSM ceremony" },
];

const POLICY_BY_TYPE: Record<WalletType, string> = {
  hot: "Instant signing enabled",
  warm: "Approval required before signing",
  cold: "Multi-sig + HSM signing ceremony",
};

const INITIAL: WalletRequestValues = {
  name: "",
  description: "",
  type: "hot",
  group: "Operations",
  tags: "",
  network: "Sandbox Ledger",
  asset: "SBX",
  provider: "Mesh (VaultCore)",
};

function ChoiceCard({
  selected,
  onClick,
  title,
  meta,
  badge,
  disabled,
  titleSuffix,
  check,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  meta: string;
  badge?: React.ReactNode;
  disabled?: boolean;
  titleSuffix?: React.ReactNode;
  check?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={cn(
        "mb-2.5 flex w-full items-center gap-3.5 rounded-lg border bg-surface px-4 py-3.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft",
        selected ? "border-tint-info-border bg-tint-info" : "border-line hover:border-primary",
        disabled && "cursor-not-allowed opacity-85 hover:border-line"
      )}
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-info-soft text-[15px] font-bold text-info">{title.charAt(0)}</span>
      <span className="min-w-0">
        <span className="flex items-center gap-2 text-sm font-bold text-ink">
          {title}
          {titleSuffix}
        </span>
        <span className="mt-0.5 block text-[13px] text-muted">{meta}</span>
      </span>
      {badge}
      {check && (
        <svg aria-hidden className="ml-auto size-[18px] shrink-0 text-primary" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 10.5l3.5 3.5L15 6" />
        </svg>
      )}
    </button>
  );
}

function ConfirmSheet({
  values,
  networks,
  providers,
  onConfirm,
  onClose,
}: {
  values: WalletRequestValues;
  networks: typeof WALLET_NETWORKS;
  providers: typeof WALLET_PROVIDERS;
  onConfirm: (walletId?: string) => void;
  onClose: () => void;
}) {
  const [typed, setTyped] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const matches = typed === values.name;

  const confirm = async () => {
    setLoading(true);
    const selectedNetwork = networks.find((n) => n.id === values.network);
    const selectedProvider = providers.find((p) => p.id === values.provider);
    const result = await createWalletRequest(values.name, {
      chain: selectedNetwork?.chain,
      network: selectedNetwork?.apiNetwork,
      walletProfileId: selectedProvider?.profileId,
    });
    setLoading(false);
    if (result.ok) {
      onConfirm(result.walletId);
    } else {
      toast({ variant: "error", title: "Wallet creation failed", description: result.error ?? "Unknown error" });
    }
  };

  return (
    <DialogPrimitive.Root open onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-[14px] bg-surface p-6 shadow-soft outline-none">
          <DialogPrimitive.Title className="text-base font-bold text-ink">Confirm wallet creation</DialogPrimitive.Title>
          <DialogPrimitive.Description className="mt-1 text-xs leading-relaxed text-muted">
            This creates a network-visible wallet identity and writes an immutable audit record.
          </DialogPrimitive.Description>

          <div className="mt-4 overflow-hidden rounded-lg border border-line">
            {[
              { k: "Label", v: values.name },
              { k: "Type", v: values.type.charAt(0).toUpperCase() + values.type.slice(1) },
              { k: "Group", v: values.group },
              { k: "Chain / Network", v: values.network },
              { k: "Primary asset", v: values.asset },
              { k: "Provider / Route", v: values.provider.includes("fireblocks") ? "Fireblocks"
                : values.provider.includes("sandbox-") || values.provider.includes("sepolia-") ? "Alloy Mesh (VaultCore)"
                : values.provider },
            ].map((row) => (
              <div key={row.k} className="flex items-center justify-between gap-4 border-b border-line px-4 py-2.5 text-xs last:border-b-0">
                <span className="shrink-0 text-muted">{row.k}</span>
                <span className="text-right font-semibold text-ink">{row.v}</span>
              </div>
            ))}
          </div>

          <InfoBanner variant="warning" className="mt-4 mb-0">
            Creating a wallet is a consequential action. It cannot be undone while the request is pending approval.
          </InfoBanner>

          <div className="mt-4">
            <Field label={`Type “${values.name}” to confirm`} htmlFor="cf-typed" error={typed.length > 0 && !matches ? "Name does not match" : undefined}>
              <Input id="cf-typed" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={values.name} autoComplete="off" />
            </Field>
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <DialogPrimitive.Close asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogPrimitive.Close>
            <Button variant="primary" disabled={!matches} loading={loading} onClick={confirm}>
              {loading ? "Provisioning wallet…" : "Confirm"}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function ReviewRow({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-5 border-b border-line py-2.5 text-sm last:border-b-0">
      <span className="text-muted">{k}</span>
      <span className={cn("max-w-[60%] text-right font-semibold text-ink", mono && "font-mono text-[13px]")}>{v}</span>
    </div>
  );
}

function CreatedStatus({
  id,
  values,
  events,
  requestedBy,
  policy,
  onReset,
}: {
  id: string;
  values: WalletRequestValues;
  events: WalletCreationEvent[];
  requestedBy: string;
  policy: string;
  onReset?: () => void;
}) {
  const dotClass: Record<string, string> = {
    done: "bg-success",
    active: "bg-primary ring-4 ring-primary-ring",
    pending: "bg-line-soft",
  };
  const config = [
    { k: "Wallet ID", v: id },
    { k: "Label", v: values.name },
    { k: "Chain / Network", v: values.network },
    { k: "Type", v: values.type.charAt(0).toUpperCase() + values.type.slice(1) },
    { k: "Provider / Route", v: values.provider },
    { k: "Requested By", v: requestedBy },
    { k: "Policy", v: policy },
  ];
  return (
    <>
      <Breadcrumb items={[{ label: "Wallets", href: "/wallets" }, { label: values.name }]} />

      <div className="mb-7 grid grid-cols-1 overflow-hidden rounded-lg border border-line bg-surface sm:grid-cols-3">
        <div className="px-5 py-[18px]">
          <div className="mb-1 text-2xl font-bold leading-[1.15] tracking-tight text-primary">Step 3 of 4</div>
          <div className="text-[13px] text-muted">Creation Progress</div>
        </div>
        <div className="border-t border-line px-5 py-[18px] sm:border-l sm:border-t-0">
          <div className="mb-1 text-2xl font-bold leading-[1.15] tracking-tight text-ink">{values.network}</div>
          <div className="text-[13px] text-muted">Target Chain</div>
        </div>
        <div className="border-t border-line px-5 py-[18px] sm:border-l sm:border-t-0">
          <div className="mb-1 text-2xl font-bold leading-[1.15] tracking-tight text-ink">{values.provider}</div>
          <div className="text-[13px] text-muted">Provider</div>
        </div>
      </div>

      <h2 className="mb-4 text-[15px] font-bold text-ink">Creation Timeline</h2>
      <div className="mb-7 rounded-lg border border-line bg-surface px-5">
        {events.map((e, i) => (
          <div key={e.title} className={cn("grid grid-cols-[16px_1fr] gap-4 py-[18px]", i < events.length - 1 && "border-b border-line")}>
            <span aria-hidden className={cn("mt-0.5 size-4 rounded-full", dotClass[e.state])} />
            <div>
              <div className="text-sm font-bold text-ink">{e.title}</div>
              <div className="mt-0.5 text-[13px] font-medium text-muted">{e.meta}</div>
              {e.detail && <div className="mt-1 text-[13px] leading-relaxed text-muted">{e.detail}</div>}
            </div>
          </div>
        ))}
      </div>

      <h2 className="mb-4 text-[15px] font-bold text-ink">Wallet Configuration</h2>
      <div className="max-w-[600px] overflow-hidden rounded-lg border border-line bg-surface">
        <table className="w-full border-collapse text-sm">
          <tbody>
            {config.map((row, i) => (
              <tr key={row.k} className={cn(i < config.length - 1 && "border-b border-line")}>
                <td className="w-[150px] px-5 py-3 font-medium text-muted">{row.k}</td>
                <td className={cn("px-5 py-3 text-right font-semibold text-ink", row.k === "Wallet ID" && "font-mono")}>{row.v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-7 flex flex-wrap gap-2">
        <Link to="/wallets" className={buttonVariants({ variant: "primary" })}>
          View wallets →
        </Link>
        {onReset && (
          <Button variant="secondary" onClick={onReset}>
            Create another wallet
          </Button>
        )}
      </div>
    </>
  );
}

export default function WalletRequestFlow({
  initialStep,
  viewId,
}: {
  initialStep: WalletRequestStep;
  viewId?: string;
}) {
  const navigate = useNavigate();
  const canCreate = usePermission("wallet:create");
  const wallets = useWalletStore((s) => s.wallets);
  const pendingDetails = useWalletStore((s) => s.pendingDetails);

  const [values, setValues] = React.useState<WalletRequestValues>(INITIAL);
  const [phase, setPhase] = React.useState<1 | 2 | 3 | 4 | "created">(
    initialStep === "created" ? "created" : initialStep === "confirm" ? 4 : initialStep
  );
  const [confirmOpen, setConfirmOpen] = React.useState(initialStep === "confirm");
  const [createdId, setCreatedId] = React.useState<string | null>(null);
  const [networks, setNetworks] = React.useState(WALLET_NETWORKS);
  const [providers, setProviders] = React.useState(WALLET_PROVIDERS);
  const [walletAssets, setWalletAssets] = React.useState(WALLET_ASSETS);

  React.useEffect(() => {
    let cancelled = false;
    fetchWalletOptions().then((opts) => {
      if (cancelled || !opts) return;
      const apiNets = opts.networks.filter((n) => n.enabled).map((n) => ({
        id: `${n.chain}/${n.network}`,
        label: n.network === "public-sandbox" ? "Sandbox" : n.network.charAt(0).toUpperCase() + n.network.slice(1),
        sub: `${n.network} · ${n.chain}`,
        glyph: n.chain === "sandbox-ledger" ? "⛓" : "Ξ",
        chain: n.chain,
        apiNetwork: n.network,
      }));
      const apiProvs = opts.providerProfiles.filter((p) => p.enabled).map((p) => {
        const pid = p.walletProfileId;
        const isFireblocks = pid.includes("fireblocks");
        const isSigning = pid.includes("signing");
        return {
          id: pid,
          name: isFireblocks ? "Fireblocks" : p.displayName,
          networks: apiNets.map((n) => n.id),
          model: isFireblocks
            ? "Custody + MPC signing · Fireblocks"
            : isSigning ? "Testnet signing · Mesh" : "Sandbox · No signing",
          profileId: pid,
        };
      });
      if (apiNets.length > 0) {
        const existingIds = new Set(WALLET_NETWORKS.map((n) => n.id));
        const merged = [...apiNets.filter((n) => !existingIds.has(n.id)), ...WALLET_NETWORKS];
        setNetworks(merged);
        const existingPIds = new Set(WALLET_PROVIDERS.map((p) => p.id));
        setProviders([...apiProvs.filter((p) => !existingPIds.has(p.id)), ...WALLET_PROVIDERS]);
        const apiAssetList = apiNets.map((n) => ({
          symbol: n.apiNetwork === "sepolia" ? "tETH" : "SBX",
          name: n.apiNetwork === "sepolia" ? "Test Ether" : "Sandbox Token",
          network: n.id,
        }));
        const existingAIds = new Set(WALLET_ASSETS.map((a) => `${a.symbol}/${a.network}`));
        setWalletAssets([...apiAssetList.filter((a) => !existingAIds.has(`${a.symbol}/${a.network}`)), ...WALLET_ASSETS]);
        setValues((v) => v.network === INITIAL.network ? { ...v, network: merged[0].id, asset: apiAssetList[0]?.symbol ?? v.asset, provider: "" } : v);
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Viewing an existing pending wallet from the Wallets list (?step=created&id=...)
  // rather than having just finished the wizard in this session.
  const viewed = viewId ? pendingDetails[viewId.toUpperCase()] : undefined;
  const viewedWallet = viewId ? wallets.find((w) => w.id.toUpperCase() === viewId.toUpperCase()) : undefined;
  if (viewId && viewed && viewedWallet) {
    return (
      <div className="max-w-[1112px]">
        <CreatedStatus
          id={viewedWallet.id}
          values={viewed.values}
          events={viewed.events}
          requestedBy={viewed.requestedBy}
          policy={viewed.policy}
        />
      </div>
    );
  }

  const set = <K extends keyof WalletRequestValues>(key: K, value: WalletRequestValues[K]) =>
    setValues((v) => ({ ...v, [key]: value }));

  const assets = walletAssets.filter((a) => a.network === values.network);

  const selectNetwork = (network: string) => {
    const nextAssets = walletAssets.filter((a) => a.network === network);
    const provider = providers.find((p) => p.id === values.provider);
    setValues((v) => ({
      ...v,
      network,
      asset: nextAssets.some((a) => a.symbol === v.asset) ? v.asset : (nextAssets[0]?.symbol ?? ""),
      provider: provider && !provider.networks.includes(network) ? "" : v.provider,
    }));
  };

  const canContinue =
    phase === 1
      ? values.name.trim().length > 0 && values.group.length > 0
      : phase === 2
        ? values.network.length > 0 && values.asset.length > 0
        : phase === 3
          ? values.provider.length > 0
          : true;

  const hint =
    phase === 1
      ? "Enter a wallet name and choose a group to continue."
      : phase === 2
        ? "Select a network and a primary asset to continue."
        : phase === 3
          ? "Choose a custody provider to continue."
          : "";

  const go = (n: 1 | 2 | 3 | 4) => setPhase(n);
  const next = () => setPhase((p) => (typeof p === "number" && p < 4 ? ((p + 1) as 1 | 2 | 3 | 4) : p));

  const created = phase === "created";
  // A bare `?step=created` deep link with no prior submit in this session (and no
  // ?id=) reproduces the seeded WLT-007 fixture, matching the documented Figma frame.
  const fallbackPending = pendingDetails["WLT-007"];
  const createdMeta = createdId ? pendingDetails[createdId] : fallbackPending;
  const createdWalletId = createdId ?? "WLT-007";

  return (
    <div className={created ? "max-w-[1112px]" : "max-w-[900px]"}>
      {!created && (
        <>
          <Breadcrumb items={[{ label: "Wallets", href: "/wallets" }, { label: "Wallet Creation" }]} />
          <Stepper steps={STEPS.map((label) => ({ label }))} current={phase - 1} className="mb-6" />
        </>
      )}

      {created ? (
        <CreatedStatus
          id={createdWalletId}
          values={createdId ? values : fallbackPending.values}
          events={createdMeta.events}
          requestedBy={createdMeta.requestedBy}
          policy={createdMeta.policy}
          onReset={() => {
            setValues(INITIAL);
            setCreatedId(null);
            setPhase(1);
          }}
        />
      ) : (
        <div className="rounded-lg border border-line bg-surface p-6">
          {phase === 1 && (
            <div className="flex flex-col gap-5">
              <h2 className="text-[17px] font-bold text-ink">Configuration</h2>
              <Field label="Wallet name" htmlFor="w-name" required hint="Shown across the wallet list and detail views.">
                <Input id="w-name" value={values.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. HT Solana Ops" />
              </Field>
              <Field label="Description" htmlFor="w-desc" hint="Optional. Record the wallet's operational purpose.">
                <Textarea id="w-desc" rows={3} value={values.description} onChange={(e) => set("description", e.target.value)} />
              </Field>
              <div>
                <div className="mb-2 text-xs font-semibold text-ink">
                  Wallet type<span className="ml-0.5 text-danger">*</span>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {TYPE_CARDS.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      aria-pressed={values.type === t.value}
                      onClick={() => set("type", t.value)}
                      className={cn(
                        "rounded-lg border p-3.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft",
                        values.type === t.value ? "border-primary bg-selected-soft" : "border-line hover:border-primary"
                      )}
                    >
                      <div className="text-sm font-bold text-ink">{t.label}</div>
                      <div className="mt-1 text-xs leading-relaxed text-muted">{t.description}</div>
                    </button>
                  ))}
                </div>
              </div>
              <Field label="Group" htmlFor="w-group" required>
                <select
                  id="w-group"
                  value={values.group}
                  onChange={(e) => set("group", e.target.value)}
                  className="h-[36px] w-full rounded-sm border border-line-strong bg-surface px-3 text-sm text-ink outline-none focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)]"
                >
                  <option value="">Select a group…</option>
                  {WALLET_GROUPS.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Tags" htmlFor="w-tags" hint="Comma-separated, optional.">
                <Input id="w-tags" value={values.tags} onChange={(e) => set("tags", e.target.value)} placeholder="ops, solana, production" />
              </Field>
            </div>
          )}

          {phase === 2 && (
            <div className="flex flex-col gap-5">
              <h2 className="text-[17px] font-bold text-ink">Network & Asset</h2>
              <div>
                <div className="mb-2 text-xs font-semibold text-ink">Network</div>
                {networks.map((n) => (
                  <ChoiceCard
                    key={n.id}
                    title={n.label}
                    meta={n.sub}
                    selected={values.network === n.id}
                    onClick={() => selectNetwork(n.id)}
                    check={values.network === n.id}
                  />
                ))}
              </div>
              <div>
                <div className="mb-2 text-xs font-semibold text-ink">Primary asset</div>
                <div className="overflow-hidden rounded-lg border border-line">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-line bg-bg text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">
                        <th className="px-4 py-2.5">Asset</th>
                        <th className="px-4 py-2.5">Network</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assets.map((a) => (
                        <tr
                          key={a.symbol}
                          onClick={() => set("asset", a.symbol)}
                          className={cn("cursor-pointer border-b border-line last:border-b-0", values.asset === a.symbol && "bg-tint-info")}
                        >
                          <td className="px-4 py-3 text-sm text-ink">
                            <span className="mr-2 font-semibold">{a.symbol}</span>— {a.name}
                          </td>
                          <td className="px-4 py-3 text-sm text-muted">{a.network}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-2 text-xs text-muted">Addresses are generated on creation.</p>
              </div>
            </div>
          )}

          {phase === 3 && (
            <div className="flex flex-col gap-4">
              <h2 className="text-[17px] font-bold text-ink">Provider Route</h2>
              {providers.map((p) => {
                const compatible = p.networks.includes(values.network);
                const isApiFireblocks = p.id.includes("fireblocks");
                const isMesh = p.id === "Mesh (VaultCore)" || p.id.includes("sandbox-") || p.id.includes("sepolia-");
                return (
                  <ChoiceCard
                    key={p.id}
                    title={isApiFireblocks ? "Fireblocks" : isMesh ? "Alloy Mesh" : p.name}
                    meta={compatible ? `${p.model} · Compatible with ${values.network}` : `Not available on ${values.network}`}
                    selected={values.provider === p.id}
                    disabled={!compatible}
                    onClick={() => set("provider", p.id)}
                    check={values.provider === p.id}
                    titleSuffix={
                      isApiFireblocks ? <Badge variant="info" dot={false} size="sm">MPC</Badge>
                        : isMesh ? <span className="text-muted">(VaultCore)</span> : undefined
                    }
                  />
                );
              })}
              <InfoBanner variant="info" className="mb-0">
                The selected provider holds custody of keys. No screen displays raw key material.
              </InfoBanner>
            </div>
          )}

          {phase === 4 && (
            <div className="flex flex-col">
              <h2 className="text-[17px] font-bold text-ink">Policy & Confirm</h2>

              <h3 className="mb-2 mt-5 text-[15px] font-bold text-ink">Configuration</h3>
              <div className="rounded-lg border border-line bg-surface px-4">
                <ReviewRow k="Wallet name" v={values.name} />
                <ReviewRow k="Type" v={values.type.charAt(0).toUpperCase() + values.type.slice(1)} />
                <ReviewRow k="Group" v={values.group} />
                <ReviewRow k="Tags" v={values.tags || "—"} />
                <ReviewRow k="Description" v={values.description || "—"} />
              </div>

              <h3 className="mb-2 mt-5 text-[15px] font-bold text-ink">Network & Asset</h3>
              <div className="rounded-lg border border-line bg-surface px-4">
                <ReviewRow k="Network" v={values.network} />
                <ReviewRow k="Primary asset" v={values.asset} mono />
                <ReviewRow k="Address" v="Generated on creation" />
              </div>

              <h3 className="mb-2 mt-5 text-[15px] font-bold text-ink">Provider Route</h3>
              <div className="rounded-lg border border-line bg-surface px-4">
                <ReviewRow k="Provider / Route" v={
                  values.provider.includes("fireblocks") ? "Fireblocks"
                    : values.provider.includes("sandbox-") || values.provider.includes("sepolia-") ? "Alloy Mesh (VaultCore)"
                    : values.provider
                } />
                <ReviewRow k="Signer model" v={providers.find((p) => p.id === values.provider)?.model ?? "—"} />
              </div>

              <h3 className="mb-2 mt-5 text-[15px] font-bold text-ink">Policy Preview</h3>
              <div className="rounded-lg border border-success-border bg-success-soft px-4 py-3">
                <div className="text-[13px] font-bold text-success-strong">✓ POL-003 — Wallet Creation Policy</div>
                <p className="mt-1 text-[13px] leading-relaxed text-text">
                  {POLICY_BY_TYPE[values.type]} — {values.group} policy group. New chain activation requires admin
                  approval (2-of-3 quorum).
                </p>
              </div>

              <h3 className="mb-2 mt-5 text-[15px] font-bold text-ink">Screening & Authority</h3>
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-3 rounded-lg border border-line bg-surface px-4 py-2.5">
                  <span className="text-[13px] text-muted">RiskGuard screening</span>
                  <Badge variant="success" dot={false} size="sm">
                    Low risk
                  </Badge>
                </div>
                <div className="flex items-center justify-between gap-3 rounded-lg border border-line bg-surface px-4 py-2.5">
                  <span className="text-[13px] text-muted">Authority</span>
                  <span className="text-right text-[13px] font-semibold text-ink">Org Admin · Maya Okonkwo · wallet:create</span>
                </div>
              </div>

              <InfoBanner variant="warning" className="mt-5 mb-0" title="Consequential action">
                Creating a wallet publishes a network-visible identity and writes an immutable audit record.
              </InfoBanner>
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            {phase === 1 ? (
              <Link to="/wallets" className={buttonVariants({ variant: "secondary" })}>
                Cancel
              </Link>
            ) : (
              <Button variant="secondary" onClick={() => go((phase - 1) as 1 | 2 | 3)}>
                ← Back
              </Button>
            )}
            <div className="flex items-center gap-3">
              {!canContinue && hint && <span className="text-xs text-muted">{hint}</span>}
              {phase < 4 ? (
                <Button variant="primary" disabled={!canContinue || !canCreate} onClick={() => canCreate && next()}>
                  {phase === 1 ? "Next: Network & Asset →" : phase === 2 ? "Next: Provider Route →" : "Next: Policy & Confirm →"}
                </Button>
              ) : (
                <Button variant="primary" disabled={!canCreate} onClick={() => canCreate && setConfirmOpen(true)}>
                  Create wallet
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmOpen && (
        <ConfirmSheet
          values={values}
          networks={networks}
          providers={providers}
          onClose={() => setConfirmOpen(false)}
          onConfirm={(walletId) => {
            if (!canCreate) return;
            setConfirmOpen(false);
            toast({
              variant: "success",
              title: "Wallet created",
              description: walletId
                ? `${values.name} created (${walletId}).`
                : `${values.name} was created.`,
            });
            navigate(walletId ? `/wallets/${walletId}` : "/wallets");
          }}
        />
      )}
    </div>
  );
}
